/**
 * The tour offer (SPEC §8, reference 06): the owner's welcome, Start / Skip /
 * Don't show again, and the resume one click away for visitors who only need
 * that. Replayed from Help › Tour, it drops the first-visit parts: no Don't
 * show again, and Cancel for Skip.
 *
 * The welcome is the owner's copy, from the seed (`profile.welcome`), and the
 * one place the editor speaks in a person's voice rather than in labels.
 * Without one, the offer is just the Editor Tour.
 */
import { useEffect, useRef, useState } from "react";
import { useEditor } from "../EditorContext";
import { FileIcon } from "../ui/icons";
import type { EditorLayout } from "../shell/layout";
import "../ui/controls.css";
import "../ui/modal.css";
import "./tour.css";

export interface TourOfferProps {
  readonly variant: "firstRun" | "replay";
  readonly onStart: () => void;
  /** `dontShowAgain`: the visitor ticked "Don't show again". */
  readonly onClose: (dontShowAgain: boolean) => void;
  /** The editor's layout, which the offer's diagram draws (desktop by default). */
  readonly layout?: EditorLayout;
}

/** The offer's title when the seed has no welcome. */
const EDITOR_TOUR = "Editor Tour";

export function TourOffer({ variant, onStart, onClose, layout = "desktop" }: TourOfferProps) {
  const firstRun = variant === "firstRun";
  const { links, content } = useEditor();
  const welcome = content.seed.profile.welcome;
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const startRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    startRef.current?.focus();
  }, []);

  return (
    <div className="kn-modal__scrim kn-tour-offer__scrim">
      <div
        className="kn-tour-offer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kn-tour-offer-title"
        aria-describedby="kn-tour-offer-body"
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose(dontShowAgain);
        }}
      >
        <LayoutDiagram layout={layout} />
        <div className="kn-tour-offer__content">
          <h2 id="kn-tour-offer-title">{welcome?.title ?? EDITOR_TOUR}</h2>
          <div id="kn-tour-offer-body" className="kn-tour-offer__body">
            {welcome?.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
          <div className="kn-tour-offer__actions">
            {firstRun && (
              <label className="kn-tour-offer__dont">
                <input
                  type="checkbox"
                  className="kn-checkbox"
                  checked={dontShowAgain}
                  onChange={(event) => {
                    setDontShowAgain(event.target.checked);
                  }}
                />
                Don&apos;t show again
              </label>
            )}
            <span className="kn-tour__spacer" />
            <button
              type="button"
              className="kn-button"
              onClick={() => {
                onClose(dontShowAgain);
              }}
            >
              {firstRun ? "Skip" : "Cancel"}
            </button>
            <button
              ref={startRef}
              type="button"
              className="kn-button kn-button--primary"
              onClick={onStart}
            >
              Start tour
            </button>
          </div>
        </div>
        <footer className="kn-tour-offer__footer">
          <FileIcon size={13} />
          Just looking for the resume?
          <a href={links.resume} download>
            Download Resume.pdf
          </a>
        </footer>
      </div>
    </div>
  );
}

/**
 * The editor's layout in outline, the viewport marked: panels either side on
 * desktop, a phone with the viewport over the panel sheet in the compact
 * layout. Decoration; the dialog's text says everything it does.
 */
function LayoutDiagram({ layout }: { layout: EditorLayout }) {
  if (layout === "compact") {
    return (
      <div className="kn-tour-offer__art" data-layout="compact" aria-hidden="true">
        <div className="kn-tour-offer__phone">
          <span className="kn-tour-offer__phone-bar" />
          <span className="kn-tour-offer__art-viewport">Viewport</span>
          <span>Panels</span>
        </div>
      </div>
    );
  }
  return (
    <div className="kn-tour-offer__art" data-layout="desktop" aria-hidden="true">
      <span>Outliner</span>
      <span className="kn-tour-offer__art-viewport">Viewport</span>
      <span>Inspector</span>
    </div>
  );
}
